import type { ReactNode } from 'react';

export interface AdminTableColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
}

interface AdminTableProps<T> {
  columns: AdminTableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 rounded bg-gray-200 w-3/4" />
        </td>
      ))}
    </tr>
  );
}

export function AdminTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  emptyMessage = 'Nessun dato trovato',
  onRowClick,
}: AdminTableProps<T>) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead>
          <tr className="bg-gray-50">
            {columns.map((col) => (
              <th
                key={col.key}
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={columns.length} />
            ))
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-text-secondary"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, i) => (
              <tr
                key={(row.id as string) ?? i}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`transition-colors ${
                  i % 2 === 1 ? 'bg-gray-50/50' : ''
                } ${onRowClick ? 'cursor-pointer hover:bg-primary-50/50' : 'hover:bg-gray-50'}`}
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3 text-text-primary whitespace-nowrap">
                    {col.render
                      ? col.render(row)
                      : (row[col.key] as ReactNode) ?? '-'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
